/*
 * The `AaSequence*` types are in the schema and in every union their `Protein*` counterparts belong
 * to, but the `Protein*` types hold the `matches_sqla_model` claim, so nothing resolves to the new
 * names and no response contains one. Queries that still select only `... on Protein*` therefore
 * generate an `AaSequence*` branch with no fields on it, which stops consumers of the union
 * compiling.
 *
 * Narrowing those branches away describes what the server returns today. Grep for this name to find
 * every place that has to change when the queries select both names (BNCH-169122).
 */
type UnresolvedAaSequenceTypename =
  | 'AaSequenceNode'
  | 'AaSequenceVersionNode'
  | 'AaSequenceAnnotationVersionNode'
  | 'AaSequenceAlignmentNode';

/*
 * Codegen merges branches that select the same fields, so an unresolved name can arrive either on
 * its own or sharing a branch with another fieldless member such as `InaccessibleInfoNode`. Drop
 * the branch in the first case and narrow its `__typename` in the second, leaving branches that
 * name none of the new types exactly as they were.
 */
type WithoutUnresolvedAaSequenceNode<T> = T extends {__typename: UnresolvedAaSequenceTypename}
  ? never
  : T extends {__typename: infer TTypename}
    ? [Extract<TTypename, UnresolvedAaSequenceTypename>] extends [never]
      ? T
      : Omit<T, '__typename'> & {__typename: Exclude<TTypename, UnresolvedAaSequenceTypename>}
    : T;

/*
 * Array form, for the common case of handing a query result to `accessibleObjs` and friends.
 * Identity at runtime, because there is nothing to filter out: the narrowing states which branches
 * the server can actually return, it does not remove any.
 */
export const withoutUnresolvedAaSequenceNodes = <T extends {__typename: string}>(
  values: ReadonlyArray<T>
): Array<WithoutUnresolvedAaSequenceNode<T>> => values as Array<WithoutUnresolvedAaSequenceNode<T>>;

/*
 * Value form, for a single polymorphic field rather than a list.
 */
export const withoutUnresolvedAaSequenceNode = <T extends {__typename: string}>(
  value: T
): WithoutUnresolvedAaSequenceNode<T> => value as WithoutUnresolvedAaSequenceNode<T>;

export default WithoutUnresolvedAaSequenceNode;
